import { Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useFieldArray } from 'react-hook-form'

import {
  Collection,
  CollectionNft,
  CollectionNftInput,
  CollectionStatus,
  ItemNftSalesType,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { CreateNftStatus } from '@/components/organisms'
import {
  CollectionInputDateCell,
  CollectionThumbnailInputCell,
  CreateCollectionProps,
  FileUploadInfo,
  TextInputCell,
  ToggleInputCell,
} from '@/components/pages/ConsoleCollectionCreate/template/CollectionCreateForm'
import {
  Form,
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

export type CollectionEditFormProps = {
  artistId: string
  collection: Maybe<
    Pick<
      Collection,
      | 'id'
      | 'title'
      | 'description'
      | 'thumbnail'
      | 'nft'
      | 'isPublishOnStore'
      | 'startedAt'
      | 'endedAt'
      | 'status'
    >
  >
  fileUploadInfo: FileUploadInfo
  onSubmit: (values: EditCollectionProps) => Promise<void>
}

export type EditCollectionProps = CreateCollectionProps & {
  isPublishedFlag: boolean
}

const schema: UseFormProps['schema'] = o => ({
  title: o.string().max(100).label(tLabel('consoleCollectionEdit.title')),
  description: o
    .string()
    .max(500)
    .label(tLabel('consoleCollectionEdit.description')),
  startedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleCollectionEdit.startedAt')),
  endedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleCollectionEdit.endedAt')),
  thumbnailPath: o
    .string()
    .label(tLabel('consoleCollectionEdit.thumbnailPath')),
  thumbnailResource: o.object().allow(null).optional(),
  hasNft: o.boolean().allow(false).optional(),
  isPublishedFlag: o.boolean().allow(false).optional(),
  isPublishOnStore: o.boolean().allow(false).optional(),
  nft: o.object({
    salesType: o.string().label(tLabel('consoleCollectionEdit.nft.salesType')),
    chains: o.array().items(
      o.object({
        id: o.string().allow('').optional(),
        chainName: o.string().allow('').optional(),
        royalty: o
          .number()
          .min(0)
          .max(20)
          .label(tLabel('consoleCollectionEdit.nft.royalty')),
      })
    ),
  }),
})

export const CollectionEditForm: Organism<CollectionEditFormProps> = ({
  artistId,
  onSubmit,
  fileUploadInfo,
  collection,
}) => {
  const { t } = useTranslation('consoleCollectionEdit')
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false)
  const [createItemStatus, setCreateItemStatus] = useState<CreateNftStatus>(
    CreateNftStatus.Creating
  )
  const [createNftStatus, setCreateNftStatus] = useState<CreateNftStatus>(
    CreateNftStatus.Creating
  )
  const [error, setError] = useState<string>('')
  const { showError } = useMessage()
  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    control,
    watch,
    reset,
  } = useForm<EditCollectionProps>({
    schema,
    defaultValues,
  })
  const watchHasNft = watch('hasNft', !!collection?.nft)
  const isPublishedFlag =
    collection?.status === CollectionStatus.Published ? true : false
  const { fields } = useFieldArray({
    control,
    name: 'nft.chains',
  })

  useEffect(() => {
    if (collection?.nft) {
      return
    }
    if (!watchHasNft) {
      setValue('nft', defaultNft, { shouldValidate: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchHasNft, collection?.nft])

  useEffect(() => {
    if (collection) {
      reset({
        title: collection.title,
        description: collection.description ?? '',
        thumbnailPath: collection.thumbnail?.uri ?? '',
        isPublishOnStore: !!collection.isPublishOnStore,
        startedAt: collection.startedAt ?? null,
        endedAt: collection.endedAt ?? null,
        isPublishedFlag: isPublishedFlag,
        thumbnailResource: collection.thumbnail?.uri
          ? {
              id: '',
              resourceName: '',
              imageUrl: collection.thumbnail.uri,
              imageUploadPath: collection.thumbnail.uri,
            }
          : null,
        ...buildNft(collection.nft),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection])

  const handleOnSubmit = useCallback(
    async (values: EditCollectionProps) => {
      const inputs = {
        ...values,
        nft: values.hasNft ? values.nft : null,
        status: values.isPublishedFlag
          ? CollectionStatus.Published
          : CollectionStatus.Unpublished,
        startedAt: values.startedAt === '' ? null : values.startedAt,
        endedAt: values.endedAt === '' ? null : values.endedAt,
      }
      if (values.hasNft) {
        setCreateItemStatus(CreateNftStatus.Creating)
        setCreateNftStatus(CreateNftStatus.Creating)
        setOpenCreateModal(true)
        return
      }
      await onSubmit(inputs)
        .then(() => {
          setOpenCreateModal(false)
          setCreateItemStatus(CreateNftStatus.Completed)
          setCreateNftStatus(CreateNftStatus.Completed)
        })
        .catch(error => {
          setCreateItemStatus(CreateNftStatus.Error)
          setCreateNftStatus(CreateNftStatus.Error)
          setError(error.message)
          if (!values.hasNft) {
            handleError<EditCollectionProps>(error, setError, showError)
          }
        })
    },
    [onSubmit, showError]
  )

  return (
    <OrganismFrame sx={{ pt: 5 }}>
      <Form onSubmit={handleSubmit(handleOnSubmit)} id="collection-edit-form">
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 2,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          error={errors?.title}
          {...register('title')}
        />
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 5,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          error={errors?.description}
          {...register('description')}
        />
        <CollectionThumbnailInputCell
          artistId={artistId}
          watch={watch}
          setValue={setValue}
          error={errors.thumbnailPath}
          thumbnailUploadInfo={fileUploadInfo}
        />
        <ToggleInputCell
          content={{
            title: t('isPublishedFlag'),
            description: (
              <Typography variant="caption" color="text.secondary">
                {t('isPublishedFlagDesc')}
              </Typography>
            ),
          }}
          {...register('isPublishedFlag')}
          checked={watch('isPublishedFlag')}
        />
        <CollectionInputDateCell
          errors={errors}
          setValue={setValue}
          startedAt={watch('startedAt')}
          endedAt={watch('endedAt')}
        />
        <ToggleInputCell
          content={{
            title: t('isPublishOnStore'),
            description: t('isPublishOnStoreDesc'),
          }}
          checked={!!watch('isPublishOnStore')}
          {...register('isPublishOnStore')}
        />
      </Form>
    </OrganismFrame>
  )
}

function buildNft(
  nft?: Maybe<CollectionNft>
): Pick<EditCollectionProps, 'hasNft' | 'nft'> {
  return {
    hasNft: !!nft,
    nft: {
      salesType: nft?.salesType ?? ItemNftSalesType.UserActivation,
      chains: [
        {
          chainName: nft?.chains?.[0]?.chainName ?? '',
          royalty: nft?.chains?.[0]?.royalty ?? 10,
        },
      ],
    },
  }
}

const defaultNft: CollectionNftInput = {
  salesType: ItemNftSalesType.UserActivation,
  chains: [
    {
      chainName: '',
      royalty: 10,
    },
  ],
}

const defaultValues: EditCollectionProps = {
  title: '',
  thumbnailPath: '',
  description: '',
  thumbnailResource: null,
  nft: defaultNft,
  hasNft: false,
  isPublishOnStore: false,
  startedAt: null,
  endedAt: null,
  isPublishedFlag: false,
}
